import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { CheckCircle2, Loader2, ArrowLeft, Upload, X } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const fieldCls = "bg-[#0A0A0A] border-[#262626] focus-visible:border-[#0055FF] focus-visible:ring-0 text-white rounded-none h-12 placeholder:text-[#5a5a5a]";

const empty = {
  client_name: "", client_email: "", current_weight: "", sleep: "", energy: "",
  hunger: "", workout_completion: "", cardio_completion: "", notes: "",
};

const readFile = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export default function CheckIn() {
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onPhotos = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 4);
    try {
      const encoded = await Promise.all(files.map(readFile));
      setPhotos((prev) => [...prev, ...encoded].slice(0, 4));
    } catch {
      toast.error("Could not read one of the images");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_name.trim() || !form.client_email.trim() || !form.current_weight.trim()) {
      toast.error("Name, email, and current weight are required.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/checkins", { ...form, photos });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-16 px-6">
      <div className="max-w-2xl mx-auto">
        <button onClick={() => navigate("/")} data-testid="checkin-back" className="inline-flex items-center gap-2 text-sm text-[#A3A3A3] hover:text-white mb-8 transition-colors">
          <ArrowLeft size={16} /> Back to site
        </button>
        <div className="mb-8">
          <span className="text-xs uppercase tracking-[0.25em] text-[#0055FF] font-bold">Weekly Check-In</span>
          <h1 className="font-display font-semibold uppercase tracking-tight text-white text-3xl sm:text-4xl mt-3">Submit Your Progress</h1>
          <p className="mt-3 text-[#A3A3A3]">Fill this out each week so I can review your progress and adjust your plan.</p>
        </div>

        {done ? (
          <div data-testid="checkin-success" className="bg-[#141414] border border-[#0055FF] p-10 text-center shadow-[0_0_40px_rgba(0,85,255,0.15)]">
            <CheckCircle2 size={48} className="text-[#0055FF] mx-auto" />
            <h3 className="font-display font-semibold text-2xl text-white mt-6">Check-In Received</h3>
            <p className="mt-4 text-[#A3A3A3]">Thanks for staying accountable. I&rsquo;ll review your check-in and follow up with any adjustments.</p>
            <button onClick={() => { setForm(empty); setPhotos([]); setDone(false); }} className="mt-6 bg-[#0055FF] text-white hover:bg-[#3377FF] font-bold px-6 py-3 uppercase tracking-wide text-sm transition-colors">
              Submit Another
            </button>
          </div>
        ) : (
          <form data-testid="checkin-form" onSubmit={submit} className="bg-[#141414] border border-[#262626] p-6 sm:p-10 space-y-6">
            <div className="grid sm:grid-cols-2 gap-6">
              <Field label="Full Name *"><Input data-testid="checkin-name" className={fieldCls} value={form.client_name} onChange={set("client_name")} /></Field>
              <Field label="Email *"><Input data-testid="checkin-email" type="email" className={fieldCls} value={form.client_email} onChange={set("client_email")} /></Field>
              <Field label="Current Weight *"><Input data-testid="checkin-weight" className={fieldCls} value={form.current_weight} onChange={set("current_weight")} placeholder="e.g. 205 lbs" /></Field>
              <Field label="Sleep (avg hrs / quality)"><Input data-testid="checkin-sleep" className={fieldCls} value={form.sleep} onChange={set("sleep")} placeholder="e.g. 7 hrs, good" /></Field>
              <Field label="Energy Level"><Input data-testid="checkin-energy" className={fieldCls} value={form.energy} onChange={set("energy")} placeholder="Low / Medium / High" /></Field>
              <Field label="Hunger Level"><Input data-testid="checkin-hunger" className={fieldCls} value={form.hunger} onChange={set("hunger")} placeholder="Low / Medium / High" /></Field>
              <Field label="Workouts Completed"><Input data-testid="checkin-workout" className={fieldCls} value={form.workout_completion} onChange={set("workout_completion")} placeholder="e.g. 4 of 4" /></Field>
              <Field label="Cardio Completed"><Input data-testid="checkin-cardio" className={fieldCls} value={form.cardio_completion} onChange={set("cardio_completion")} placeholder="e.g. 3 of 3" /></Field>
            </div>
            <Field label="Questions or Concerns">
              <Textarea data-testid="checkin-notes" className={`${fieldCls} h-28 py-3`} value={form.notes} onChange={set("notes")} placeholder="Anything you want me to know this week..." />
            </Field>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-[#A3A3A3] font-bold">Progress Photos (up to 4)</Label>
              <label data-testid="checkin-photo-input" className="flex items-center gap-2 cursor-pointer bg-[#0A0A0A] border border-dashed border-[#262626] hover:border-[#0055FF] transition-colors px-4 py-4 text-sm text-[#A3A3A3]">
                <Upload size={16} /> Tap to upload photos
                <input type="file" accept="image/*" multiple className="hidden" onChange={onPhotos} />
              </label>
              {photos.length > 0 && (
                <div className="flex flex-wrap gap-3 mt-3">
                  {photos.map((p, i) => (
                    <div key={i} className="relative w-20 h-24">
                      <img src={p} alt={`Progress ${i + 1}`} className="w-full h-full object-cover border border-[#262626]" />
                      <button type="button" onClick={() => setPhotos((prev) => prev.filter((_, idx) => idx !== i))} className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-0.5">
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button type="submit" data-testid="checkin-submit" disabled={submitting} className="w-full bg-[#0055FF] text-white hover:bg-[#3377FF] transition-colors font-bold px-8 py-4 uppercase tracking-wide flex items-center justify-center gap-2 disabled:opacity-60">
              {submitting ? <><Loader2 size={18} className="animate-spin" /> Submitting...</> : "Submit Check-In"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-2">
    <Label className="text-xs uppercase tracking-widest text-[#A3A3A3] font-bold">{label}</Label>
    {children}
  </div>
);
